"use client"

import { useState, useEffect, useMemo, useCallback, memo, useRef } from "react"
import {
  getFactionInfo,
  getFactionOptions,
  getShipImageUrl,
  getMapName,
  getRankImageUrl,
  getServerName,
} from "../constants/darkorbit-constants"
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Collapse,
  TableSortLabel,
  Autocomplete,
} from "@mui/material"
import {
  FilterList as FilterListIcon,
  GroupWork as GroupWorkIcon,
  Map as MapIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  People as PeopleIcon,
  Storage as ServerIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  KeyboardArrowDown as LoadMoreIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material"
import { useInRangePlayers } from "../context/InRangePlayersContext"
import { useWebSocket } from "../context/WebSocketContext"

const ROWS_PER_PAGE = 50
const DEBOUNCE_DELAY = 1000

// Performance optimizations
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Pre-compute indexes for fast filtering
const createPlayerIndexes = (players) => {
  const factionIndex = new Map()
  const mapIndex = new Map()
  const serverIndex = new Map()
  const clanIndex = new Map()
  const usernameIndex = new Map()

  players.forEach(player => {
    // Faction index
    if (!factionIndex.has(player.faction_id)) {
      factionIndex.set(player.faction_id, [])
    }
    factionIndex.get(player.faction_id).push(player)

    // Map index
    if (!mapIndex.has(player.map_id)) {
      mapIndex.set(player.map_id, [])
    }
    mapIndex.get(player.map_id).push(player)

    // Server index
    if (!serverIndex.has(player.server_instance_id)) {
      serverIndex.set(player.server_instance_id, [])
    }
    serverIndex.get(player.server_instance_id).push(player)

    // Clan index
    if (player.clan_tag) {
      const clanKey = player.clan_tag.toLowerCase()
      if (!clanIndex.has(clanKey)) {
        clanIndex.set(clanKey, [])
      }
      clanIndex.get(clanKey).push(player)
    }

    // Username index
    const usernameKey = player.username.toLowerCase()
    if (!usernameIndex.has(usernameKey)) {
      usernameIndex.set(usernameKey, [])
    }
    usernameIndex.get(usernameKey).push(player)
  })

  return { factionIndex, mapIndex, serverIndex, clanIndex, usernameIndex }
}

// Simplified PlayerRow component with reduced complexity
const PlayerRow = memo(({ player, isFavourite, onToggleFavourite }) => {
  const theme = useTheme()
  const handleToggleFavourite = useCallback(() => {
    onToggleFavourite(player.user_id)
  }, [onToggleFavourite, player.user_id])

  // Pre-compute values to avoid repeated calculations
  const factionInfo = useMemo(() => getFactionInfo(player.faction_id), [player.faction_id])
  const serverName = useMemo(() => getServerName(player.server_instance_id), [player.server_instance_id])
  const mapName = useMemo(() => getMapName(player.map_id), [player.map_id])

  return (
    <TableRow
      hover
      sx={{
        backgroundColor: isFavourite
          ? theme.palette.mode === "dark"
            ? alpha(theme.palette.warning.dark, 0.15)
            : alpha(theme.palette.warning.light, 0.2)
          : "transparent",
        "&:hover": {
          backgroundColor: isFavourite
            ? theme.palette.mode === "dark"
              ? alpha(theme.palette.warning.dark, 0.25)
              : alpha(theme.palette.warning.light, 0.3)
            : theme.palette.mode === "dark"
              ? alpha(theme.palette.primary.dark, 0.05)
              : alpha(theme.palette.primary.light, 0.05),
        },
      }}
    >
      <TableCell>
        <Tooltip title={isFavourite ? "Remove from favourites" : "Add to favourites"} arrow>
          <IconButton
            size="small"
            onClick={handleToggleFavourite}
            sx={{
              color: isFavourite ? "warning.main" : "action.disabled",
              "&:hover": {
                color: "warning.main",
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
              },
            }}
          >
            {isFavourite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ maxWidth: "16ch" }}>
        <Tooltip title={`User ID: ${player.user_id}`} arrow>
          <Typography variant="body2" color="text.primary" fontWeight="medium" noWrap>
            {player.username}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        {player.daily_rank && (
          <Tooltip title={`Rank ${player.daily_rank}`} arrow>
            <Box
              component="img"
              src={getRankImageUrl(player.daily_rank)}
              alt={`Rank ${player.daily_rank}`}
              sx={{
                width: 24,
                height: 24,
                objectFit: "contain",
              }}
              onError={(e) => {
                e.target.style.display = "none"
              }}
            />
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        {player.clan_tag && (
          <Tooltip title={`Clan ID: ${player.clan_id || "N/A"}`} arrow>
            <Chip label={player.clan_tag} size="small" color="secondary" sx={{ height: 20, fontSize: "0.7rem" }} />
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        <Box
          component="img"
          src={factionInfo.logoUrl}
          alt={factionInfo.name}
          sx={{
            height: 24,
            width: "auto",
            objectFit: "contain",
            borderRadius: 1,
          }}
          onError={(e) => {
            e.target.style.display = "none"
          }}
        />
      </TableCell>
      <TableCell>
        <Tooltip title={`Raw: (${player.position_x}, ${player.position_y})`} arrow>
          <Typography
            variant="body2"
            color="text.primary"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "120px",
              fontFamily: "monospace",
              fontSize: "0.75rem",
            }}
          >
            {player.formattedPosition?.toString() || "N/A"}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        {player.type_id ? (
          <Tooltip title={player.type_id.replace("ship_", "")} arrow>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                component="img"
                src={getShipImageUrl(player.type_id)}
                alt={player.type_id}
                sx={{
                  height: 24,
                  width: "auto",
                  objectFit: "contain",
                  borderRadius: 1,
                }}
                onError={(e) => {
                  e.target.style.display = "none"
                }}
              />
            </Box>
          </Tooltip>
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.7rem",
              padding: "2px 6px",
              borderRadius: "4px",
              backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[200],
              color: "text.primary",
              fontWeight: "500",
              display: "inline-block",
            }}
          >
            Unknown
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Tooltip title={`Server ID: ${player.server_instance_id}`} arrow>
          <Chip
            label={serverName}
            size="small"
            variant="outlined"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              fontFamily: "sans-serif",
              minWidth: "auto",
              maxWidth: 150,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "inline-block",
              verticalAlign: "middle",
            }}
          />
        </Tooltip>
      </TableCell>
      <TableCell>
        <Tooltip title={`Map ID: ${player.map_id}`} arrow>
          <Box
            sx={{
              maxWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontFamily: "monospace",
              fontSize: "0.8rem",
              color: "text.secondary",
            }}
          >
            {mapName || `Map ${player.map_id}`}
          </Box>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Tooltip title={new Date(player.updated_at).toISOString()} arrow>
          <Typography variant="body2" noWrap color="text.primary">
            {player.formattedLastSeen}
          </Typography>
        </Tooltip>
      </TableCell>
    </TableRow>
  )
})

PlayerRow.displayName = "PlayerRow"

// Memoized GroupHeader to prevent unnecessary re-renders
const GroupHeader = memo(({ group, isExpanded, onToggle, visibleCount, theme }) => {
  const handleClick = useCallback(() => {
    onToggle(group.key)
  }, [onToggle, group.key])

  if (group.key === "ungrouped") {
    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: alpha(theme.palette.primary.light, 0.1),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PeopleIcon color="primary" />
          <Typography variant="h6" fontWeight="medium" color="text.primary">
            All Players ({group.players.length})
          </Typography>
        </Box>
        {visibleCount < group.players.length && (
          <Typography variant="body2" color="text.secondary">
            Showing {visibleCount} of {group.players.length}
          </Typography>
        )}
      </Box>
    )
  }

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 2,
        backgroundColor: alpha(theme.palette.primary.light, 0.1),
        cursor: "pointer",
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.light, 0.2),
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {isExpanded ? (
          <ExpandLessIcon fontSize="small" sx={{ mr: 1, color: "primary.main" }} />
        ) : (
          <ExpandMoreIcon fontSize="small" sx={{ mr: 1, color: "primary.main" }} />
        )}
        <GroupWorkIcon sx={{ mr: 1, color: "primary.main" }} />
        <Typography variant="h6" fontWeight="medium" color="text.primary">
          {group.name} ({group.players.length} players)
        </Typography>
      </Box>
      {isExpanded && visibleCount < group.players.length && (
        <Typography variant="body2" color="text.secondary">
          Showing {visibleCount} of {group.players.length}
        </Typography>
      )}
    </Box>
  )
})

GroupHeader.displayName = "GroupHeader"

const InRangePlayersPage = () => {
  const theme = useTheme()
  const { inRangePlayers, __ } = useInRangePlayers()
  const { subscribeToInRangePlayers, unsubscribeFromInRangePlayers, wsStatus } = useWebSocket()
  const [favourites, setFavourites] = useState(new Set())
  const [usernameCache, setUsernameCache] = useState(new Map())

  // Separate immediate and debounced filters
  const [immediateFilters, setImmediateFilters] = useState({
    faction: null,
    mapId: null,
    serverId: null,
    timeFilter: null,
    showFavouritesOnly: false,
  })

  const [searchFilters, setSearchFilters] = useState({
    clanName: "",
    playerSearch: "",
  })

  // Debounced search filters
  const debouncedSearchFilters = {
    clanName: useDebounce(searchFilters.clanName, DEBOUNCE_DELAY),
    playerSearch: useDebounce(searchFilters.playerSearch, DEBOUNCE_DELAY),
  }

  // Combine all filters
  const filters = useMemo(() => ({
    ...immediateFilters,
    ...debouncedSearchFilters
  }), [immediateFilters, debouncedSearchFilters])

  const [grouping, setGrouping] = useState({
    byFaction: false,
    byMap: false,
    byServer: false,
  })
  const [expandedGroups, setExpandedGroups] = useState({})
  const [visibleRows, setVisibleRows] = useState({}) // Track visible rows per group
  const [sortBy, setSortBy] = useState("lastSeen") // 'lastSeen', 'username', etc.
  const [sortOrder, setSortOrder] = useState("desc") // 'asc', 'desc'
  const [updateCountdown, setUpdateCountdown] = useState(20)
  const [waitingCountdown, setWaitingCountdown] = useState(20)
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date())
  const [updateStatus, setUpdateStatus] = useState("counting") // 'counting', 'waiting', 'timeout'

  // Pre-compute player indexes for faster filtering
  const playerIndexes = useMemo(() => {
    if (!inRangePlayers.length) return null
    return createPlayerIndexes(inRangePlayers)
  }, [inRangePlayers])

  // Memoized toggle favourite callback
  const toggleFavourite = useCallback((userId) => {
    setFavourites((prev) => {
      const newFavourites = new Set(prev)
      if (newFavourites.has(userId)) {
        newFavourites.delete(userId)
      } else {
        newFavourites.add(userId)
      }
      // Save to localStorage
      try {
        localStorage.setItem("player-favourites", JSON.stringify(Array.from(newFavourites)))
      } catch (error) {
        console.error("Error saving favourites:", error)
      }
      return newFavourites
    })
  }, [])

  // Load favourites from localStorage
  const loadFavourites = useCallback(() => {
    try {
      const storedFavourites = localStorage.getItem("player-favourites")
      if (storedFavourites) {
        setFavourites(new Set(JSON.parse(storedFavourites)))
      }
    } catch (error) {
      console.error("Error loading favourites:", error)
    }
  }, [])

  // Load username cache from localStorage
  const loadUsernameCache = useCallback(() => {
    try {
      const storedCache = localStorage.getItem("player-username-cache")
      if (storedCache) {
        setUsernameCache(new Map(JSON.parse(storedCache)))
      }
    } catch (error) {
      console.error("Error loading username cache:", error)
    }
  }, [])

  // Save username cache to localStorage
  const saveUsernameCache = useCallback((cache) => {
    try {
      localStorage.setItem("player-username-cache", JSON.stringify(Array.from(cache.entries())))
    } catch (error) {
      console.error("Error saving username cache:", error)
    }
  }, [])

  // Memoized filter options to prevent recalculation
  const mapOptions = useMemo(() => {
    const maps = new Set()
    inRangePlayers.forEach((p) => p.map_id && maps.add(p.map_id))
    return Array.from(maps)
      .sort()
      .map((id) => ({
        value: id,
        label: getMapName(id) || `Map ${id}`,
      }))
  }, [inRangePlayers])

  const serverOptions = useMemo(() => {
    const servers = new Set()
    inRangePlayers.forEach((p) => p.server_instance_id && servers.add(p.server_instance_id))
    return Array.from(servers)
      .sort()
      .map((id) => ({
        value: id,
        label: getServerName(id) || `Server ${id}`,
        searchText: `${getServerName(id) || `Server ${id}`} ${id}`.toLowerCase(),
      }))
  }, [inRangePlayers])

  // Pre-compute search terms to avoid repeated toLowerCase calls
  const searchTerms = useMemo(() => ({
    clanName: filters.clanName ? filters.clanName.toLowerCase() : null,
    playerSearch: filters.playerSearch ? filters.playerSearch.toLowerCase() : null,
  }), [filters.clanName, filters.playerSearch])

  // Pre-compute time cutoff to avoid repeated calculations
  const timeCutoff = useMemo(() => {
    return filters.timeFilter ? Date.now() - filters.timeFilter * 60 * 1000 : null
  }, [filters.timeFilter])

  // Optimized filtering with early exit strategies
  const filteredPlayers = useMemo(() => {
    if (!inRangePlayers.length) return []

    // Check if we have any filters at all
    const hasFilters = filters.showFavouritesOnly ||
                      filters.faction ||
                      filters.mapId ||
                      filters.serverId ||
                      filters.timeFilter ||
                      searchTerms.clanName ||
                      searchTerms.playerSearch

    if (!hasFilters) return inRangePlayers

    // Use array reduce with early exit for better performance
    const result = []
    const playersLength = inRangePlayers.length

    for (let i = 0; i < playersLength; i++) {
      const player = inRangePlayers[i]

      // Favourites filter - fastest first (Set lookup is O(1))
      if (filters.showFavouritesOnly && !favourites.has(player.user_id)) continue

      // Exact match filters - very fast (primitive comparisons)
      if (filters.faction && player.faction_id !== filters.faction) continue
      if (filters.mapId && player.map_id !== filters.mapId) continue
      if (filters.serverId && player.server_instance_id !== filters.serverId) continue

      // Time filter - moderate cost (Date object creation)
      if (timeCutoff && new Date(player.updated_at).getTime() < timeCutoff) continue

      // String search filters - most expensive, do last
      if (searchTerms.clanName) {
        if (!(player.clan_tag?.toLowerCase().includes(searchTerms.clanName) ||
              player.clan_id?.toString().includes(searchTerms.clanName))) continue
      }

      if (searchTerms.playerSearch) {
        if (!(player.username.toLowerCase().includes(searchTerms.playerSearch) ||
              player.user_id.toString().includes(searchTerms.playerSearch))) continue
      }

      result.push(player)
    }

    return result
  }, [inRangePlayers, filters.showFavouritesOnly, filters.faction, filters.mapId, filters.serverId, timeCutoff, searchTerms.clanName, searchTerms.playerSearch, favourites])

  // Optimized sorting - separate from filtering for better memoization
  const sortedPlayers = useMemo(() => {
    if (!filteredPlayers.length) return []

    // Pre-compute sort values to avoid repeated calculations
    const playersWithSortKeys = filteredPlayers.map(player => {
      const isFav = favourites.has(player.user_id)
      let sortValue

      if (sortBy === "username") {
        sortValue = player.username.toLowerCase()
      } else {
        // Default: lastSeen - use timestamp directly to avoid Date object creation
        sortValue = new Date(player.updated_at).getTime()
      }

      return { player, isFav, sortValue }
    })

    // Sort with pre-computed values
    return playersWithSortKeys.sort((a, b) => {
      // Priority sort: favourites first
      if (a.isFav !== b.isFav) return a.isFav ? -1 : 1

      // Secondary sort by criteria
      if (sortBy === "username") {
        return sortOrder === "asc" ?
          (a.sortValue < b.sortValue ? -1 : a.sortValue > b.sortValue ? 1 : 0) :
          (a.sortValue > b.sortValue ? -1 : a.sortValue < b.sortValue ? 1 : 0)
      } else {
        return sortOrder === "asc" ? a.sortValue - b.sortValue : b.sortValue - a.sortValue
      }
    }).map(item => item.player)
  }, [filteredPlayers, sortBy, sortOrder, favourites])

  // Optimized grouping with better memoization
  const groups = useMemo(() => {
    const hasGrouping = grouping.byFaction || grouping.byMap || grouping.byServer

    if (!hasGrouping) {
      return [{ key: "ungrouped", name: "All Players", players: sortedPlayers }]
    }

    // Use Map for O(1) lookups
    const groupsMap = new Map()

    // Pre-build caches to avoid repeated function calls
    const factionCache = new Map()
    const mapCache = new Map()
    const serverCache = new Map()

    // Batch process players to minimize cache lookups
    const playersLength = sortedPlayers.length
    for (let i = 0; i < playersLength; i++) {
      const player = sortedPlayers[i]
      let groupKey = ""
      let groupNameParts = []

      if (grouping.byFaction) {
        groupKey += `f${player.faction_id}_`
        if (!factionCache.has(player.faction_id)) {
          factionCache.set(player.faction_id, getFactionInfo(player.faction_id).name)
        }
        groupNameParts.push(`${factionCache.get(player.faction_id)} Faction`)
      }

      if (grouping.byMap) {
        groupKey += `m${player.map_id}_`
        if (!mapCache.has(player.map_id)) {
          mapCache.set(player.map_id, getMapName(player.map_id) || `Map ${player.map_id}`)
        }
        groupNameParts.push(mapCache.get(player.map_id))
      }

      if (grouping.byServer) {
        groupKey += `s${player.server_instance_id}_`
        if (!serverCache.has(player.server_instance_id)) {
          serverCache.set(player.server_instance_id, getServerName(player.server_instance_id) || `Server ${player.server_instance_id}`)
        }
        groupNameParts.push(serverCache.get(player.server_instance_id))
      }

      let group = groupsMap.get(groupKey)
      if (!group) {
        group = {
          key: groupKey,
          name: groupNameParts.join(" • "),
          players: [],
        }
        groupsMap.set(groupKey, group)
      }

      group.players.push(player)
    }

    // Convert to array and sort by group size
    return Array.from(groupsMap.values()).sort((a, b) => b.players.length - a.players.length)
  }, [sortedPlayers, grouping.byFaction, grouping.byMap, grouping.byServer])

  // Initialize expanded groups and visible rows when groups change
  useEffect(() => {
    const newExpandedGroups = {}
    const newVisibleRows = {}
    groups.forEach((group) => {
      newExpandedGroups[group.key] = false
      newVisibleRows[group.key] = ROWS_PER_PAGE
    })
    setExpandedGroups(newExpandedGroups)
    setVisibleRows(newVisibleRows)
  }, [groups, grouping.byFaction, grouping.byMap, grouping.byServer])

  // Memoized callbacks to prevent unnecessary re-renders
  const toggleGroup = useCallback((groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }))
  }, [])

  const loadMoreRows = useCallback((groupKey) => {
    setVisibleRows((prev) => ({
      ...prev,
      [groupKey]: (prev[groupKey] || ROWS_PER_PAGE) + ROWS_PER_PAGE,
    }))
  }, [])

  const handleGroupingChange = useCallback((groupType) => {
    setGrouping((prev) => {
      const newGrouping = { ...prev, [groupType]: !prev[groupType] }

      // Clear corresponding filters when enabling grouping
      if (newGrouping[groupType]) {
        setImmediateFilters((f) => {
          const newFilters = { ...f }
          if (groupType === "byFaction") {
            newFilters.faction = null
          } else if (groupType === "byMap") {
            newFilters.mapId = null
          } else if (groupType === "byServer") {
            newFilters.serverId = null
          }
          return newFilters
        })
      }

      return newGrouping
    })
  }, [])

  const clearFilters = useCallback(() => {
    setImmediateFilters({
      faction: null,
      mapId: null,
      serverId: null,
      timeFilter: null,
      showFavouritesOnly: false,
    })
    setSearchFilters({
      clanName: "",
      playerSearch: "",
    })
    setGrouping({
      byFaction: false,
      byMap: false,
      byServer: false,
    })
  }, [])

  const handleSort = useCallback(
    (column) => {
      if (sortBy === column) {
        setSortOrder(sortOrder === "asc" ? "desc" : "asc")
      } else {
        setSortBy(column)
        setSortOrder("desc")
      }
    },
    [sortBy, sortOrder],
  )

  const handleRefresh = useCallback(() => {
    window.location.reload()
  }, [])

  // Handle countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      if (updateStatus === "counting") {
        setUpdateCountdown((prev) => {
          if (prev <= 1) {
            setUpdateStatus("waiting")
            setWaitingCountdown(20)
            return 0
          }
          return prev - 1
        })
      } else if (updateStatus === "waiting") {
        setWaitingCountdown((prev) => {
          if (prev <= 1) {
            setUpdateStatus("timeout")
            return 0
          }
          return prev - 1
        })
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [updateStatus])

  // Reset countdown when data updates
  useEffect(() => {
    setUpdateCountdown(30)
    setUpdateStatus("counting")
    setLastUpdateTime(new Date())
  }, [inRangePlayers])

  // Update username cache when new player data is received
  useEffect(() => {
    if (inRangePlayers.length > 0) {
      setUsernameCache((prevCache) => {
        const newCache = new Map(prevCache)
        let hasChanges = false

        inRangePlayers.forEach((player) => {
          if (player.user_id && player.username && !newCache.has(player.user_id)) {
            newCache.set(player.user_id, player.username)
            hasChanges = true
          }
        })

        if (hasChanges) {
          saveUsernameCache(newCache)
        }

        return newCache
      })
    }
  }, [inRangePlayers, saveUsernameCache])

  // Subscribe to in-range players updates when component mounts
  useEffect(() => {
    if (wsStatus === "connected") {
      subscribeToInRangePlayers()
    }

    return () => {
      // Unsubscribe when component unmounts
      if (wsStatus === "connected") {
        unsubscribeFromInRangePlayers()
      }
    }
  }, [wsStatus, subscribeToInRangePlayers, unsubscribeFromInRangePlayers])

  // Initial load
  useEffect(() => {
    loadFavourites()
    loadUsernameCache()

    // Listen for localStorage changes from other tabs
    const handleStorageChange = (e) => {
      if (e.key === "player-favourites") {
        loadFavourites()
      } else if (e.key === "player-username-cache") {
        loadUsernameCache()
      }
    }

    window.addEventListener("storage", handleStorageChange)
    return () => window.removeEventListener("storage", handleStorageChange)
  }, [loadFavourites, loadUsernameCache])

  const hasActiveFilters = useMemo(
    () =>
      Object.values(immediateFilters).some(v => v !== null && v !== false) ||
      Object.values(debouncedSearchFilters).some(v => v !== "") ||
      Object.values(grouping).some(v => v),
    [immediateFilters, debouncedSearchFilters, grouping],
  )

  // Final result combining filtered and sorted players
  const finalData = useMemo(() => ({
    filteredPlayers: sortedPlayers,
    groups
  }), [sortedPlayers, groups])

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            In-Range Players
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor players currently in your vicinity
            {favourites.size > 0 && (
              <Chip
                icon={<StarIcon />}
                label={`${favourites.size} favourites`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ ml: 2 }}
              />
            )}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Tooltip title={`Last updated: ${lastUpdateTime.toLocaleTimeString()}`}>
            <Chip
              label={
                updateStatus === "counting"
                  ? `Next update in: ${updateCountdown}s`
                  : updateStatus === "waiting"
                    ? `Waiting for update... (${waitingCountdown}s)`
                    : "Took too long"
              }
              size="small"
              color={
                updateStatus === "timeout"
                  ? "error"
                  : updateStatus === "waiting"
                    ? "warning"
                    : updateCountdown > 5
                      ? "default"
                      : "warning"
              }
              sx={{
                fontWeight: "medium",
                animation:
                  updateStatus === "waiting" || (updateStatus === "counting" && updateCountdown <= 5)
                    ? "pulse 1s infinite"
                    : "none",
                "@keyframes pulse": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                  "100%": { opacity: 1 },
                },
              }}
            />
          </Tooltip>
          {updateStatus === "timeout" && (
            <Button variant="outlined" size="small" onClick={handleRefresh} startIcon={<RefreshIcon />} color="error">
              Refresh Page
            </Button>
          )}
        </Box>
      </Box>

      {/* Enhanced Filter Controls */}
      <Card
        sx={{
          mb: 3,
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
              : theme.palette.background.paper,
          border:
            theme.palette.mode === "dark"
              ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
              : `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" color="text.primary">
              Filters & Grouping
            </Typography>
            {hasActiveFilters && (
              <Chip
                label={`${Object.values(immediateFilters).filter(v => v !== null && v !== false).length + Object.values(debouncedSearchFilters).filter(v => v !== "").length + Object.values(grouping).filter(Boolean).length} active`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          {hasActiveFilters && (
            <Button size="small" onClick={clearFilters} startIcon={<ClearIcon />} sx={{ color: "text.secondary" }}>
              Clear All
            </Button>
          )}
        </Box>

        <CardContent sx={{ pt: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            {/* Faction Filter */}
            <FormControl size="small" fullWidth disabled={grouping.byFaction}>
              <InputLabel>Faction</InputLabel>
              <Select
                value={filters.faction || ""}
                onChange={(e) => setImmediateFilters((f) => ({ ...f, faction: e.target.value || null }))}
                label="Faction"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.background.default, 0.6)
                        : theme.palette.background.default,
                  },
                }}
              >
                <MenuItem value="">
                  <em>All Factions</em>
                </MenuItem>
                {getFactionOptions().map((faction) => (
                  <MenuItem key={faction.value} value={faction.value}>
                    {faction.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Map Filter */}
            <FormControl size="small" fullWidth disabled={grouping.byMap}>
              <InputLabel>Map</InputLabel>
              <Select
                value={filters.mapId || ""}
                onChange={(e) => setImmediateFilters((f) => ({ ...f, mapId: e.target.value || null }))}
                label="Map"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.background.default, 0.6)
                        : theme.palette.background.default,
                  },
                }}
              >
                <MenuItem value="">
                  <em>All Maps</em>
                </MenuItem>
                {mapOptions.map((map) => (
                  <MenuItem key={map.value} value={map.value}>
                    {map.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Server Filter */}
            <Autocomplete
              size="small"
              fullWidth
              disabled={grouping.byServer}
              options={serverOptions}
              value={serverOptions.find((option) => option.value === filters.serverId) || null}
              onChange={(event, newValue) => {
                setImmediateFilters((f) => ({ ...f, serverId: newValue ? newValue.value : null }))
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Server"
                  placeholder="Type server name or ID..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.background.default, 0.6)
                          : theme.palette.background.default,
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ServerIcon fontSize="small" color="action" />
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({option.value})
                    </Typography>
                  </Box>
                </Box>
              )}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options
                const searchTerm = inputValue.toLowerCase()
                return options.filter((option) => option.searchText.includes(searchTerm))
              }}
              noOptionsText="No servers found"
              clearOnBlur={false}
              selectOnFocus
              handleHomeEndKeys
            />

            {/* Player Search */}
            <TextField
              label="Search Player"
              value={searchFilters.playerSearch}
              onChange={(e) => setSearchFilters((f) => ({ ...f, playerSearch: e.target.value }))}
              size="small"
              placeholder="Username or User ID"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchFilters.playerSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchFilters((f) => ({ ...f, playerSearch: "" }))}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.background.default, 0.6)
                      : theme.palette.background.default,
                },
              }}
            />

            {/* Clan Search */}
            <TextField
              label="Search Clan"
              value={searchFilters.clanName}
              onChange={(e) => setSearchFilters((f) => ({ ...f, clanName: e.target.value }))}
              size="small"
              placeholder="Clan name or Clan ID"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchFilters.clanName && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchFilters((f) => ({ ...f, clanName: "" }))}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.background.default, 0.6)
                      : theme.palette.background.default,
                },
              }}
            />

            {/* Time Filter */}
            <FormControl size="small" fullWidth>
              <InputLabel>Time Filter</InputLabel>
              <Select
                value={filters.timeFilter || ""}
                onChange={(e) => setImmediateFilters((f) => ({ ...f, timeFilter: e.target.value || null }))}
                label="Time Filter"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.background.default, 0.6)
                        : theme.palette.background.default,
                  },
                }}
              >
                <MenuItem value="">
                  <em>All Time</em>
                </MenuItem>
                <MenuItem value={10}>Last 10 minutes</MenuItem>
                <MenuItem value={15}>Last 15 minutes</MenuItem>
                <MenuItem value={30}>Last 30 minutes</MenuItem>
                <MenuItem value={60}>Last 1 hour</MenuItem>
                <MenuItem value={120}>Last 2 hours</MenuItem>
                <MenuItem value={360}>Last 6 hours</MenuItem>
                <MenuItem value={1440}>Last 24 hours</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Grouping Controls */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Group By
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant={grouping.byFaction ? "contained" : "outlined"}
                size="small"
                onClick={() => handleGroupingChange("byFaction")}
                startIcon={<GroupWorkIcon />}
                sx={{ fontSize: "0.75rem" }}
              >
                Faction
              </Button>
              <Button
                variant={grouping.byMap ? "contained" : "outlined"}
                size="small"
                onClick={() => handleGroupingChange("byMap")}
                startIcon={<MapIcon />}
                sx={{ fontSize: "0.75rem" }}
              >
                Map
              </Button>
              <Button
                variant={grouping.byServer ? "contained" : "outlined"}
                size="small"
                onClick={() => handleGroupingChange("byServer")}
                startIcon={<ServerIcon />}
                sx={{ fontSize: "0.75rem" }}
              >
                Server
              </Button>
            </Box>

            {/* Add Favourites Filter */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Filter Options
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant={filters.showFavouritesOnly ? "contained" : "outlined"}
                  size="small"
                  onClick={() => setImmediateFilters((f) => ({ ...f, showFavouritesOnly: !f.showFavouritesOnly }))}
                  startIcon={<StarIcon />}
                  color="warning"
                  sx={{ fontSize: "0.75rem" }}
                  disabled={favourites.size === 0}
                >
                  Favourites Only ({favourites.size})
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Favorites Management Section */}
      {favourites.size > 0 && (
        <Card
          sx={{
            mb: 3,
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(theme.palette.warning.dark, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.warning.light, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
            border:
              theme.palette.mode === "dark"
                ? `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                : `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <StarIcon color="warning" />
              <Typography variant="h6" color="text.primary">
                Favorite Players ({favourites.size})
              </Typography>
            </Box>
          </Box>
          <CardContent sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click the delete icon to remove a user from your favorites
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                maxHeight: 200,
                overflowY: "auto",
                pr: 1,
              }}
            >
              {Array.from(favourites).map((userId) => {
                const cachedUsername = usernameCache.get(userId)
                const displayLabel = cachedUsername ? `${cachedUsername} (${userId})` : userId

                return (
                  <Chip
                    key={userId}
                    label={displayLabel}
                    variant="outlined"
                    color="warning"
                    size="small"
                    deleteIcon={
                      <Tooltip title="Remove from favorites" arrow>
                        <DeleteIcon fontSize="small" />
                      </Tooltip>
                    }
                    onDelete={() => toggleFavourite(userId)}
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.warning.dark, 0.1)
                          : alpha(theme.palette.warning.light, 0.1),
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? alpha(theme.palette.warning.dark, 0.2)
                            : alpha(theme.palette.warning.light, 0.2),
                      },
                      fontFamily: cachedUsername ? "inherit" : "monospace",
                      fontSize: "0.75rem",
                      maxWidth: 250,
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                )
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Player Groups - Optimized Rendering */}
      {finalData.groups.length > 0 ? (
        finalData.groups.map((group) => {
          const currentVisibleRows = visibleRows[group.key] || ROWS_PER_PAGE
          const isExpanded = group.key === "ungrouped" || expandedGroups[group.key]

          // Only slice if group is expanded to avoid unnecessary work
          const visiblePlayers = isExpanded ? group.players.slice(0, currentVisibleRows) : []
          const hasMoreRows = group.players.length > currentVisibleRows

          return (
            <Card
              key={group.key}
              sx={{
                mb: 3,
                overflow: "hidden",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              }}
            >
              <GroupHeader
                group={group}
                isExpanded={isExpanded}
                onToggle={toggleGroup}
                visibleCount={currentVisibleRows}
                theme={theme}
              />

              <Collapse in={isExpanded} unmountOnExit>
                {isExpanded && (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? alpha(theme.palette.background.default, 0.5)
                                : alpha(theme.palette.grey[50], 0.8),
                          }}
                        >
                          <TableCell sx={{ fontWeight: "bold", color: "text.primary", width: 50 }}>
                            <Box sx={{ display: "flex", alignItems: "center", marginLeft: 0.5 }}>
                              <StarIcon fontSize="small" color="action" />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "text.primary", maxWidth: "16ch" }}>
                            <TableSortLabel
                              active={sortBy === "username"}
                              direction={sortBy === "username" ? sortOrder : "asc"}
                              onClick={() => handleSort("username")}
                              sx={{
                                color: "inherit",
                                "&.Mui-active": {
                                  color: "primary.main",
                                },
                                "&:hover": {
                                  color: "primary.main",
                                },
                                "& .MuiTableSortLabel-icon": {
                                  color: "inherit",
                                }
                              }}
                            >
                              Username
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "text.primary", width: 80 }}>Rank</TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "text.primary" }}>Clan</TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "text.primary" }}>Faction</TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "text.primary" }}>Position</TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "text.primary" }}>Ship</TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "text.primary" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <ServerIcon fontSize="small" />
                              Server
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "text.primary" }}>Map</TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "text.primary" }}>
                            <TableSortLabel
                              active={sortBy === "lastSeen"}
                              direction={sortBy === "lastSeen" ? sortOrder : "desc"}
                              onClick={() => handleSort("lastSeen")}
                              sx={{
                                color: "inherit",
                                "&.Mui-active": {
                                  color: "primary.main",
                                },
                                "&:hover": {
                                  color: "primary.main",
                                },
                                "& .MuiTableSortLabel-icon": {
                                  color: "inherit",
                                }
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <ScheduleIcon fontSize="small" />
                                Last Seen
                              </Box>
                            </TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visiblePlayers.map((player) => (
                          <PlayerRow
                            key={player.user_id}
                            player={player}
                            isFavourite={favourites.has(player.user_id)}
                            onToggleFavourite={toggleFavourite}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Load More Button */}
                {hasMoreRows && (
                  <Box
                    sx={{
                      p: 2,
                      textAlign: "center",
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.background.default, 0.3)
                          : alpha(theme.palette.grey[50], 0.5),
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => loadMoreRows(group.key)}
                      startIcon={<LoadMoreIcon />}
                      sx={{
                        color: "text.secondary",
                        borderColor: alpha(theme.palette.divider, 0.5),
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? alpha(theme.palette.primary.dark, 0.1)
                              : alpha(theme.palette.primary.light, 0.1),
                          borderColor: "primary.main",
                          color: "primary.main",
                        },
                      }}
                    >
                      Load More ({group.players.length - currentVisibleRows} remaining)
                    </Button>
                  </Box>
                )}
              </Collapse>
            </Card>
          )
        })
      ) : (
        <Card
          sx={{
            textAlign: "center",
            py: 8,
            backgroundColor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.background.paper, 0.8)
                : theme.palette.background.paper,
          }}
        >
          <CardContent>
            <PeopleIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No players match the current filters
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filter criteria
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  )
}

export default InRangePlayersPage
